import React from 'react';
import { Modal, Form, Row, Col, Button, Spinner } from 'react-bootstrap';
import { Formik } from 'formik';
import moment from 'moment';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { useMutation } from '@apollo/client';

import CustomButton from '../../../components/CustomButton';
import { UPDATE_REWARD_V2 } from '../../../apollo/reward/Mutation';
import { GET_REWARDS_V2 } from '../../../apollo/reward/Query';

const ImageThumb = ({ image }) => {
  return (
    <img
      src={URL.createObjectURL(image)}
      alt={image.name}
      style={{
        objectFit: 'contain',
        borderRadius: 8,
        height: 200,
        width: 200,
      }}
    />
  );
};

function ModalEditReward({
  show,
  onClose,
  editingReward,
  onSubmit,
  file,
  onUpload,
  userTypeLoading,
  userTypeData,
}) {
  const [updateRewardV2, { loading: updating, error: updateError }] = useMutation(UPDATE_REWARD_V2,);

  const submitUpdateReward = async (values, { setSubmitting }) => {
    try {
      // Combine date + time to ISO string
      const combineDT = (dateStr, timeStr, defaultTime) => {
        if (!dateStr && !timeStr) return null;
        const time = timeStr || defaultTime;
        const d = new Date(`${dateStr}T${time}`);
        return isNaN(d) ? null : d.toISOString();
      };

      const payload = {
        name: values.name,
        description: values.description || '',
        percent: values.percent !== '' && values.percent != null ? Number(values.percent) : null,
        pointMax: values.pointMax !== '' && values.pointMax != null ? Number(values.pointMax) : null,
        pointMin: values.pointMin !== '' && values.pointMin != null ? Number(values.pointMin) : null,
        maxDiscountAmount: values.maxDiscountAmount !== '' && values.maxDiscountAmount != null ? Number(values.maxDiscountAmount) : null,
        maxUser: values.maxUser !== '' && values.maxUser != null ? Number(values.maxUser) : null,
        remainUser: values.remainUser !== '' && values.remainUser != null ? Number(values.remainUser) : null,
        rewardType: values.rewardType,
        userType: values.userType || null,
        startDate: combineDT(
          values.startDate,
          values.startHour && values.startMinute ? `${values.startHour}:${values.startMinute}` : null,
          '00:00'
        ),
        endDate: combineDT(
          values.endDate,
          values.endHour && values.endMinute ? `${values.endHour}:${values.endMinute}` : null,
          '23:59'
        ),
        status: values.status,
      };

      console.log('Update Reward Payload:', payload);
      console.log('Status value:', values.status);

      const result = await updateRewardV2({
        variables: { data: payload, where: { id: editingReward?.id } },
        optimisticResponse: {
          updateRewardV2: {
            __typename: 'Reward',
            name: payload.name,
            description: payload.description,
            maxDiscountAmount: payload.maxDiscountAmount,
            rewardType: payload.rewardType,
            percent: payload.percent,
            pointMax: payload.pointMax,
            pointMin: payload.pointMin,
            remainUser: payload.remainUser,
            userType: payload.userType,
            maxUser: payload.maxUser,
            status: payload.status,
            startDate: payload.startDate,
            endDate: payload.endDate,
          },
        },
        update: (cache, { data }) => {
          const updatedFields = data?.updateRewardV2;
          console.log('Updated fields from mutation:', updatedFields);
          if (!updatedFields || !editingReward?.id) return;
          try {
            const existing = cache.readQuery({ query: GET_REWARDS_V2 });
            if (existing?.getRewardsV2?.data) {
              const updatedData = existing.getRewardsV2.data.map((r) =>
                r.id === editingReward.id
                  ? { ...r, ...updatedFields }
                  : r
              );
              cache.writeQuery({
                query: GET_REWARDS_V2,
                data: {
                  getRewardsV2: {
                    ...existing.getRewardsV2,
                    data: updatedData,
                  },
                },
              });
            }
          } catch (err) {
            console.error('Cache update error:', err);
          }
        },
        refetchQueries: [{ query: GET_REWARDS_V2 }],
        awaitRefetchQueries: true,
      });

      console.log('Update Reward Result:', result);

      setSubmitting(false);
      if (typeof onSubmit === 'function') {
        onSubmit(values);
      }
      if (typeof onClose === 'function') {
        onClose();
      }
    } catch (e) {
      setSubmitting(false);
      console.error('UPDATE_REWARD_V2, error', e);
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>ແກ້ໄຂລາງວັນ</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={{
            name: editingReward?.name || '',
            pointMin: editingReward?.pointMin || '',
            pointMax: editingReward?.pointMax || '',
            percent: editingReward?.percent || '',
            maxDiscountAmount: editingReward?.maxDiscountAmount || '',
            maxUser: editingReward?.maxUser || '',
            remainUser: editingReward?.remainUser || '',
            rewardType: editingReward?.rewardType || 'DISCOUNT',
            startDate: editingReward?.startDate ? moment(editingReward.startDate).format('YYYY-MM-DD') : '',
            endDate: editingReward?.endDate ? moment(editingReward.endDate).format('YYYY-MM-DD') : '',
            // add 24h time selection (hour/minute) to edit form
            startHour: editingReward?.startDate ? moment(editingReward.startDate).format('HH') : '00',
            startMinute: editingReward?.startDate ? moment(editingReward.startDate).format('mm') : '00',
            endHour: editingReward?.endDate ? moment(editingReward.endDate).format('HH') : '23',
            endMinute: editingReward?.endDate ? moment(editingReward.endDate).format('mm') : '59',
            description: editingReward?.description || '',
            status: editingReward?.status || 'USABLE',
            userType: editingReward?.userType?.id || '',
          }}
          onSubmit={submitUpdateReward}
          enableReinitialize
        >
          {({ values, handleChange, handleSubmit }) => (
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຊື່ລາງວັນ <span style={{color: 'red'}}>*</span></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={values.name}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຊື່ລາງວັນ"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຄະແນນ <span style={{color: 'red'}}>*</span></Form.Label>
                    <Form.Control
                      type="number"
                      name="pointMin"
                      style={{ width: '100%' }}
                      value={values.pointMin}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຄະແນນທີ່ຕ້ອງແລກ"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

                 <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ປະເພດລາງວັນ *</Form.Label>
                    <Form.Select
                      name="rewardType"
                      value={values.rewardType}
                      onChange={handleChange}
                      disabled={editingReward?.rewardType === 'DISCOUNT'}
                      required
                    >
                      <option value="DISCOUNT">ສ່ວນຫຼຸດ</option>
                      <option value="ITEM">ສິນຄ້າ</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຈຳນວນລາງວັນ</Form.Label>
                    <Form.Control
                      type="number"
                      name="maxUser"
                      style={{ width: '100%' }}
                      value={values.maxUser}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຈຳນວນລາງວັນ"
                      min="1"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                {/* <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຄະແນນສູງສຸດ *</Form.Label>
                    <Form.Control
                      type="number"
                      name="pointMax"
                      style={{ width: '100%' }}
                      value={values.pointMax}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຄະແນນສູງສຸດ"
                    />
                  </Form.Group>
                </Col> */}
                {
                  values.rewardType === 'DISCOUNT' && (
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ເປີເຊັນສ່ວນຫຼຸດ (%)</Form.Label>
                        <Form.Control
                          type="number"
                          name="percent"
                          style={{ width: '100%' }}
                          value={values.percent}
                          onChange={handleChange}
                          placeholder="ກະລຸນາໃສ່ເປີເຊັນສ່ວນຫຼຸດ"
                          min="0"
                          max="100"
                        />
                      </Form.Group>
                    </Col>
                  )
                }
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ຈຳນວນລາງວັນທີ່ເຫຼືອ</Form.Label>
                    <Form.Control
                      type="number"
                      name="remainUser"
                      style={{ width: '100%' }}
                      value={values.remainUser}
                      onChange={handleChange}
                      placeholder="ກະລຸນາໃສ່ຈຳນວນລາງວັນທີ່ເຫຼືອ"
                      min="0"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ປະເພດລູກຄ້າ</Form.Label>
                    <Form.Select
                      name="userType"
                      value={values.userType}
                      onChange={handleChange}
                      aria-label="Select user type"
                      disabled={userTypeLoading}
                    >
                      <option value="">
                        {userTypeLoading 
                          ? 'ກຳລັງໂຫຼດຂໍ້ມູນ...' 
                          : 'ເລືອກປະເພດລູກຄ້າ...'}
                      </option>
                      {!userTypeLoading && userTypeData?.getUserTypes
                        ?.filter((item) => item?.isOpen || item?.id === editingReward?.userType?.id)
                        .map((userType) => (
                          <option key={userType?.id} value={userType?.id}>
                            {userType?.name}
                          </option>
                        ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>ລາຍລະອຽດ</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={values.description}
                  onChange={handleChange}
                  placeholder="ກະລຸນາໃສ່ລາຍລະອຽດຂອງລາງວັນ"
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ວັນທີເລີ່ມຕົ້ນ *</Form.Label>
                    <Form.Control
                      type="date"
                      name="startDate"
                      value={values.startDate}
                      onChange={handleChange}
                      required
                    />
                    {/* time input for start (24-hour select) */}
                    <Form.Group className="mt-2">
                      <Form.Label>ເວລາເລີ່ມຕົ້ນ</Form.Label>
                      <Row>
                        <Col>
                          <Form.Select
                            name="startHour"
                            value={values.startHour}
                            onChange={handleChange}
                            aria-label="Hour"
                          >
                            {[...Array(24).keys()].map((h) => (
                              <option key={h} value={String(h).padStart(2, '0')}>
                                {String(h).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col>
                          <Form.Select
                            name="startMinute"
                            value={values.startMinute}
                            onChange={handleChange}
                            aria-label="Minute"
                          >
                            {[...Array(60).keys()].map((m) => (
                              <option key={m} value={String(m).padStart(2, '0')}>
                                {String(m).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    </Form.Group>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>ວັນທີສິ້ນສຸດ *</Form.Label>
                    <Form.Control
                      type="date"
                      name="endDate"
                      value={values.endDate}
                      onChange={handleChange}
                      required
                    />
                    {/* time input for end (24-hour select) */}
                    <Form.Group className="mt-2">
                      <Form.Label>ເວລາສິ້ນສຸດ</Form.Label>
                      <Row>
                        <Col>
                          <Form.Select
                            name="endHour"
                            value={values.endHour}
                            onChange={handleChange}
                            aria-label="Hour"
                          >
                            {[...Array(24).keys()].map((h) => (
                              <option key={h} value={String(h).padStart(2, '0')}>
                                {String(h).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col>
                          <Form.Select
                            name="endMinute"
                            value={values.endMinute}
                            onChange={handleChange}
                            aria-label="Minute"
                          >
                            {[...Array(60).keys()].map((m) => (
                              <option key={m} value={String(m).padStart(2, '0')}>
                                {String(m).padStart(2, '0')}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                      </Row>
                    </Form.Group>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ສະຖານະ</Form.Label>
                      <Form.Select
                        name="status"
                        value={values.status}
                        onChange={handleChange}
                      >
                        <option value="USABLE">ໃຊ້ງານໄດ້</option>
                        <option value="EXPIRED">ໝົດອາຍຸ</option>
                        <option value="OUT_OF_STOCK">ເຄື່ອງໝົດ</option>
                        <option value="DISABLE">ປິດການໃຊ້ງານ</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  {
                    values.rewardType !== 'ITEM' && <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>ຫຼຸດສູງສຸດ (ກີບ)</Form.Label>
                      <Form.Control
                        type="number"
                        name="maxDiscountAmount"
                        style={{ width: '100%' }}
                        value={values.maxDiscountAmount}
                        onChange={handleChange}
                        placeholder="ກະລຸນາໃສ່ຈຳນວນຫຼຸດສູງສຸດ"
                        min="0"
                        disabled={values.rewardType === 'ITEM'}
                      />
                    </Form.Group>
                  </Col>
                  }
                </Row>

              {/* <Form.Group className="mb-3">
                <Form.Label>ຮູບພາບລາງວັນ</Form.Label>
                <div style={{ textAlign: 'center' }}>
                  <input
                    type="file"
                    id="file-upload"
                    onChange={onUpload}
                    accept="image/*"
                    hidden
                  />
                  <label style={{ width: 200 }} htmlFor="file-upload">
                    <div
                      style={{
                        backgroundColor: '#F9FAFB',
                        height: '200px',
                        width: '100%',
                        border: '1px dashed #93989F',
                        borderRadius: 8,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      {file ? (
                        <ImageThumb image={file} />
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                          }}>
                          <CloudUploadOutlinedIcon
                            sx={{ fontSize: 50, color: '#93989F' }}
                          />
                          <p style={{ color: '#93989F' }}>ເພີ່ມຮູບພາບລາງວັນ</p>
                          <p
                            style={{
                              color: '#3C169B',
                              textDecoration: 'underline',
                            }}>
                            Browse
                          </p>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </Form.Group> */}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <Button variant="secondary" onClick={onClose}>
                  ຍົກເລີກ
                </Button>
                <CustomButton
                  type="submit"
                  disabled={updating}
                  title={updating ? <Spinner/> : 'ອັບເດດ'}
                  width="130px"
                  borderRadius="8px"
                />
              </div>
            </Form>
          )}
        </Formik>
      </Modal.Body>
    </Modal>
  );
}

export default ModalEditReward;